import React, { useState } from 'react';
import { XMarkIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import { useGetUploadUrlMutation } from '../../store/api/localApi';

// Mirrors the server POLICY_DOCUMENT cap: PDF only, 20MB (see
// src/uploads/purposes.ts POLICY_DOCUMENT + src/policies/utils/validate-pdf.ts).
const MAX_BYTES = 20 * 1024 * 1024;

interface Props {
  /** The policy's location. Presign `contextId` for POLICY_DOCUMENT is the
   *  LOCATION id (not the policyId — an admin picks the file before a version
   *  exists), and the server namespaces the S3 key by it, so there is no
   *  client-controlled key. */
  locationId: string;
  /** Called with the uploaded object's server-returned s3Key once the PUT
   *  succeeds, or `undefined` when the selection is cleared. The parent panel
   *  passes it to createVersion as `fileKey`. */
  onChange: (s3Key: string | undefined) => void;
  /** Called whenever an upload starts/finishes so the parent can disable
   *  Publish while a PUT is in flight. */
  onUploadingChange: (uploading: boolean) => void;
  /** testid for the file input; derived controls append a suffix. */
  inputTestId: string;
  /** Accessible name for the file input — the raw <input type="file"> has no
   *  visible label of its own, so the caller supplies one (the two call sites
   *  differ: "add the first document" vs "upload a new version"). */
  ariaLabel: string;
}

/**
 * Single-file PDF picker for a policy document version.
 *
 * Flow: presign(POLICY_DOCUMENT, contextId=locationId) → PUT the raw File to
 * the returned S3 URL → hand the returned s3Key up via onChange. The bytes
 * never pass through the API. Mirrors CredentialAttachmentInput, but PDF-only
 * because a policy version is a PDF (the confirm call HEAD-validates it).
 */
const PolicyDocumentUpload: React.FC<Props> = ({
  locationId,
  onChange,
  onUploadingChange,
  inputTestId,
  ariaLabel,
}) => {
  const [getUploadUrl] = useGetUploadUrlMutation();
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setUploadingState = (next: boolean) => {
    setUploading(next);
    onUploadingChange(next);
  };

  const clear = () => {
    setFileName(null);
    setError(null);
    setUploadingState(false);
    onChange(undefined);
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    onChange(undefined);

    if (file.type !== 'application/pdf') {
      setFileName(null);
      setError('Please choose a PDF file.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setFileName(null);
      setError('That file is too large. The maximum is 20MB.');
      return;
    }

    setFileName(file.name);
    setUploadingState(true);
    try {
      const { uploadUrl, s3Key } = await getUploadUrl({
        purpose: 'POLICY_DOCUMENT',
        contextId: locationId,
        mimeType: file.type,
        sizeBytes: file.size,
        fileName: file.name,
      }).unwrap();
      const putResp = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!putResp.ok) throw new Error(`S3 ${putResp.status}`);
      onChange(s3Key);
    } catch {
      setError('Upload failed. Please try again.');
      setFileName(null);
      onChange(undefined);
    } finally {
      setUploadingState(false);
    }
  };

  return (
    <div>
      {fileName ? (
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <PaperClipIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
          <span className="truncate" data-testid={`${inputTestId}-filename`}>
            {fileName}
          </span>
          {uploading ? (
            <span className="text-xs text-gray-500" data-testid={`${inputTestId}-uploading`}>
              Uploading…
            </span>
          ) : (
            <button
              type="button"
              data-testid={`${inputTestId}-remove`}
              onClick={clear}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Remove document"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      ) : (
        <input
          type="file"
          data-testid={inputTestId}
          aria-label={ariaLabel}
          accept="application/pdf"
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
        />
      )}

      {error && (
        <p className="mt-1 text-xs text-red-600" data-testid={`${inputTestId}-error`}>
          {error}
        </p>
      )}
    </div>
  );
};

export default PolicyDocumentUpload;
